import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { Button } from '@/common/ui/button'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { DataTableSkeleton } from '@/common/ui/table/data-table-skeleton'
import {
	DoubleArrowLeftIcon,
	DoubleArrowRightIcon,
} from '@radix-ui/react-icons'
import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	PaginationState,
	useReactTable,
} from '@tanstack/react-table'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[]
	data: TData[]
	totalItems: number
	pageSizeOptions?: number[]
	isLoading?: boolean
}

export function DataTablePaginate<TData, TValue>({
	columns,
	data,
	totalItems,
	pageSizeOptions = [10, 20, 30, 40, 50],
	isLoading = false,
}: DataTableProps<TData, TValue>) {
	const { pageSize, setPageSize, currentPage, setCurrentPage } =
		useTablePagination()

	const paginationState = {
		pageIndex: currentPage - 1, // zero-based index for React Table
		pageSize: pageSize,
	}

	const pageCount = Math.ceil(totalItems / pageSize)

	const handlePaginationChange = (
		updaterOrValue:
			| PaginationState
			| ((old: PaginationState) => PaginationState),
	) => {
		const pagination =
			typeof updaterOrValue === 'function'
				? updaterOrValue(paginationState)
				: updaterOrValue

		setCurrentPage(pagination.pageIndex + 1) // converting zero-based index to one-based
		setPageSize(pagination.pageSize)
	}

	const table = useReactTable({
		data,
		columns,
		pageCount: pageCount,
		state: {
			pagination: paginationState,
		},
		onPaginationChange: handlePaginationChange,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		manualPagination: true,
		manualFiltering: true,
	})

	return (
		<div className="space-y-4">
			<ScrollArea className="h-[calc(80dvh-220px)] rounded-md border md:h-[calc(90dvh-240px)]">
				<div className="min-w-max">
					<Table className="relative">
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header, index) => (
										<TableHead key={header.id + index} colSpan={header.colSpan}>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
										</TableHead>
									))}
								</TableRow>
							))}
						</TableHeader>

						<TableBody>
							{isLoading ? (
								<DataTableSkeleton
									columnCount={columns.length}
									rowCount={pageSize}
								/>
							) : table.getRowModel().rows?.length ? (
								table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										data-state={row.getIsSelected() && 'selected'}
										className="h-16"
									>
										{row.getVisibleCells().map((cell, index) => (
											<TableCell key={cell.id + index}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										colSpan={columns.length}
										className="h-16 text-center"
									>
										ไม่มีข้อมูล.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>

			<div className="flex flex-col items-center justify-end gap-2 space-x-2 py-4 sm:flex-row">
				<div className="flex w-full items-center justify-between">
					<div className="flex-1 text-sm text-muted-foreground">
						{totalItems > 0 ? (
							<>
								Showing{' '}
								{paginationState.pageIndex * paginationState.pageSize + 1} to{' '}
								{Math.min(
									(paginationState.pageIndex + 1) * paginationState.pageSize,
									totalItems,
								)}{' '}
								of {totalItems} entries
							</>
						) : (
							'No entries found'
						)}
					</div>
					<div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
						<div className="flex items-center space-x-2">
							<p className="whitespace-nowrap text-sm font-medium">
								Rows per page
							</p>
							<Select
								value={`${paginationState.pageSize}`}
								onValueChange={(value) => {
									table.setPageSize(Number(value))
								}}
							>
								<SelectTrigger className="h-8 w-[70px]">
									<SelectValue placeholder={paginationState.pageSize} />
								</SelectTrigger>
								<SelectContent side="top">
									{pageSizeOptions.map((pageSize) => (
										<SelectItem key={pageSize} value={`${pageSize}`}>
											{pageSize}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</div>
				<div className="flex w-full items-center justify-between gap-2 sm:justify-end">
					<div className="flex w-[150px] items-center justify-center text-sm font-medium">
						{totalItems > 0 ? (
							<>
								Page {paginationState.pageIndex + 1} of {table.getPageCount()}
							</>
						) : (
							'No pages'
						)}
					</div>
					<div className="flex items-center space-x-2">
						<Button
							aria-label="Go to first page"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(0)}
							disabled={!table.getCanPreviousPage() || isLoading}
						>
							<DoubleArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to previous page"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage() || isLoading}
						>
							<ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to next page"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage() || isLoading}
						>
							<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to last page"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(table.getPageCount() - 1)}
							disabled={!table.getCanNextPage() || isLoading}
						>
							<DoubleArrowRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
